import { format } from 'date-fns';
import { useGetMatterAboutQuery } from '@/api';
import { useAppSelector } from '@/store/hooks';
import { selectChatLastResponse, selectChatMatterInfo } from '@/store/slices/chat/chat';

const STAMP_DATE_FORMAT = 'HH:mm d MMM yyyy';

const HAS_TIMEZONE = /(Z|[+-]\d{2}:?\d{2})$/;

/** `GET /chat` returns `lastResponse` in UTC without an offset, so add it before parsing. */
const formatStampDate = (value: string | null | undefined) => {
  if (!value) return null;

  const date = new Date(HAS_TIMEZONE.test(value) ? value : `${value}Z`);
  return Number.isNaN(date.getTime()) ? null : format(date, STAMP_DATE_FORMAT);
};

/**
 * Shows when the response finished and, in a Proclaim case chat, when the case was last synced.
 * Only under the last assistant message — messages have no timestamps of their own.
 */
export const ChatResponseStamp = () => {
  const matterInfo = useAppSelector(selectChatMatterInfo);
  const lastResponse = useAppSelector(selectChatLastResponse);
  const matterId = matterInfo?.matterId;

  const { lastSyncedDatetime, source } = useGetMatterAboutQuery(
    { matterId: matterId ?? '' },
    {
      skip: !matterId,
      selectFromResult: ({ data }) => ({
        lastSyncedDatetime: data?.info.lastSyncedDatetime,
        source: data?.info.source,
      }),
    },
  );

  // Uploaded cases have no Proclaim case to sync against, so nothing about syncing is
  // rendered for them — matching the case files panel.
  const syncedAt = source === 'PROCLAIM' ? formatStampDate(lastSyncedDatetime) : null;
  const completedAt = formatStampDate(lastResponse);

  if (!completedAt && !syncedAt) return null;

  return (
    <div className="text-theme-text-grey mt-2 text-right text-[0.6875rem] leading-4">
      {[completedAt && `Completed ${completedAt}`, syncedAt && `Case synced at ${syncedAt}`]
        .filter(Boolean)
        .join(' · ')}
    </div>
  );
};
